import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Container,
  Paper,
  Tooltip,
  InputBase,
  IconButton,
  Stack,
} from "@mui/material";
import {
  BodyLarge,
  H7TitleLarge,
} from "../../components/StyledComponents/Typography/Typography.tsx";
import { StyledIconButton } from "../../components/StyledComponents/Buttons/AuradineButtons.js";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useHistory, useLocation } from "react-router-dom";
import DataGrid from "../../components/DataGrid/DataGrid.tsx";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth0 } from "@auth0/auth0-react";
import {
  QueryClient,
  QueryClientProvider,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { GetUserUseQuery } from "../../api/api.js";
import { useQuery } from "@tanstack/react-query";
import { PostJobsBySerialCS } from "../../api/api.js";
import { GridNoRowsOverlay } from "@mui/x-data-grid-pro";
import StyledLinearProgress from "../../components/StyledComponents/Progress/Progress.js";
import AuradineErrorState from "../../components/StyledComponents/EmptyStates/AuradineErrorState.tsx";
import {
  H8TitleMedium,
  BodyMedium,
} from "../../components/StyledComponents/Typography/Typography.tsx";
import { format, parseISO, isValid } from "date-fns";
import { debounce } from "lodash";
import PageWrapper from "../../components/PageWrapper/PageWrapper.js";
import { useTheme } from "../../ThemeContext/ThemeContext.js";
export const HeaderContainer = React.memo(() => {
  const navigate = useHistory();
  const { colors } = useTheme();
  return (
    <Box
      display="flex"
      flexDirection="row"
      gap={2.48}
      marginBottom={"1.5rem"}
    >
      <Box
        display="flex"
        flexDirection="column"
        minWidth={"2.98rem"}
        justifyContent="flex-start"
        alignItems={"center"}
      >
        <StyledIconButton colors={colors} onClick={() => navigate.goBack()}>
          {" "}
          <ArrowBackIosNewIcon
            fontSize="medium"
          />{" "}
        </StyledIconButton>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        gap={4}
        justifyContent="flex-start"
      >
        <H7TitleLarge>Miner Job Details</H7TitleLarge>
        <BodyLarge>
          View job details for a specific miner.
        </BodyLarge>
      </Box>
    </Box>
  );
});

const MinerJobDetailsPageHeaderCS = ({
  searchValue,
  onSearchChange,
  tableData,
}) => {
  const { colors } = useTheme();
  return (
    <Box
      display="grid"
      gridTemplateColumns="1fr auto"
      alignItems="center"
      maxWidth="22.5rem"
      minWidth="12.5rem"
      width="100%"
    >
      {
        <Paper
          component="form"
          elevation={0}
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            alignItems: "center",
            backgroundColor: colors.background.secondary, 
            borderRadius: "1.75rem",
            paddingLeft: "1rem",
            width: "100%",
            height: "100%"
          }}
        >
          <Tooltip
            disableFocusListener
            disableTouchListener
          >
            <InputBase
              sx={{ flex: 1, minWidth: 0, color: colors?.text?.primary }}
              fullWidth
              value={searchValue}
              placeholder="Search"
              onChange={(e) =>
                onSearchChange(e.target.value)
              }
              inputProps={{
                "aria-label": "search",
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                }
              }}
            />
          </Tooltip>
          <StyledIconButton colors={colors} disabled aria-label="search">
            <SearchIcon />
          </StyledIconButton>
        </Paper>
      }
    </Box>
  );
};

function MinerJobDetailsPageContentCS() {
  const { colors } = useTheme();
  //adding auth0
  const [authToken, setAuthToken] = useState("");
  const [totalCount, setTotalCount] = useState(0);

  const { getAccessTokenSilently, isAuthenticated } =
    useAuth0();
  const urlParams = new URLSearchParams(
    window.location.search
  );
  const [searchValue, setSearchValue] = useState("");
  const [dsearchValue, setDsearchValue] = useState("");

  const debounceSearch = React.useMemo(
    () =>
      debounce((searchInput) => {
        setDsearchValue(searchInput);
      }, 750),
    []
  );

  const handleSearchChange = (searchInput) => {
    console.log("Search Input:", searchInput);
    setSearchValue(searchInput);
    debounceSearch(searchInput);
  };
  const serialNo = urlParams.get("serialNo");
  const trimmedSerialNo = serialNo ? serialNo?.trim() : "";

  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (!isAuthenticated) {
          setAuthToken("");
          console.warn(
            "User is not authenticated. Fetching token..."
          );
          return;
        }
        const token = await getAccessTokenSilently({
          cacheMode: "cache-only",
          ignoreCache: true,
        });
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
    const interval = setInterval(() => {
      fetchToken();
    }, 5 * 60 * 1000); // Refresh every 15 minutes

    return () => clearInterval(interval); // Cleanup on unmount
  }, [getAccessTokenSilently, isAuthenticated]);

  //adding user role data
  const {
    data: userInfo,
    isFetched: isUserInfoFetched,
    isFetching: isUserInfoFetching,
  } = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => GetUserUseQuery(authToken),
    enabled: !!authToken,
  });
  const userRole = userInfo?.user?.role;

  let limit = 25;

  const {
    data,
    fetchNextPage,
    isFetchingNextPage,
    status,
    isFetched,
    refetch: jobsDetailsRefetch,
    isRefetching: isJobsDetailsRefetching,
    isFetching: isJobsDetailsFetching,
    isLoading: isJobsDetailsLoading,
    isError: isJobsDetailsError,
  } = useInfiniteQuery({
    queryKey: [
      "jobDetailsCS",
      trimmedSerialNo,
      dsearchValue,
      // queryRefreshVersion,
    ],
    queryFn: ({ pageParam = { skip: 0, limit } }) =>
      PostJobsBySerialCS({
        token: authToken,
        body: {
          pagination: { ...pageParam },
          serialNo: trimmedSerialNo,
          ...(dsearchValue !== ""
            ? { filter: dsearchValue }
            : {}),
        },
      }).then((res) => {
        setTotalCount(res?.count);
        return res;
      }),
    enabled: !!authToken && !!trimmedSerialNo,
    staleTime: 0,
    gcTime: 0,
    initialPageParam: { skip: 0, limit },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.flatMap(
        (page) => page.docs
      ).length;
      const totalCount = lastPage?.count || 0;
      return totalFetched >= totalCount
        ? undefined
        : { skip: totalFetched, limit };
    },
  });

  const displayTS = (updatedAt) => {
    const parsedDate = parseISO(updatedAt);
    if (isValid(parsedDate)) {
      return format(parsedDate, "MM/dd/yyyy, h:mm a");
    } else {
      return "";
    }
  };
  console.log(data);
  let tableData = [];
  let minerInfo = {}
  if (data && data.pages && data.pages.length > 0) {
    tableData = isFetched
      ? data.pages
          .filter((page) => page.docs)
          .flatMap((page, pageIndex) =>
            page.docs.map((item, index) => {
              // Generate a unique ID by combining the page index and the item index
              return {
                ...item,
                id: `${pageIndex}-${index}`,
              };
            })
          )
      : [];

    if (data.pages[0].deviceInfo){
      minerInfo = data.pages[0].deviceInfo
    }
  } else {
    tableData = [];
  }

  const tableContainerRef = React.useRef(null);

  const handleScrollEnd = () => {
    if (!isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const CustomGridRowsOverlay = () => {
    if (
      tableData.length === 0 &&
      isFetched &&
      data &&
      data.pages &&
      Array.isArray(data.pages) &&
      data.pages.length > 0 &&
      data.pages[0].status === "ok"
    ) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          sx={{
            backgroundColor: colors?.background?.paper, // Custom background color
            color: colors?.text?.muted, // Custom text color
            fontSize: "1.2rem",
            fontWeight: "bold",
          }}
        >
          No data available
        </Box>
      );
    }
    if (
      isJobsDetailsError ||
      (isFetched &&
        data &&
        data.pages &&
        Array.isArray(data.pages) &&
        data.pages.length > 0 &&
        data.pages[0].status.toLowerCase() === "error")
    ) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          sx={{
            backgroundColor: colors?.background?.paper, // Custom background color
            color: colors?.text?.muted, // Custom text color
            fontSize: "1.2rem",
            fontWeight: "bold",
          }}
        >
          <AuradineErrorState message="Oops!! Something went wrong!!" />
        </Box>
      );
    }
  };

  const commandList = [
    { label: "Mode", value: "fvmode" },
    { label: "Power Curtail", value: "powercurtail" },
    { label: "LED", value: "led" },
    { label: "FluxVision Tuning", value: "accept-remote-tuning" },
    { label: "Restart", value: "restart" },
    { label: "Firmware Upgrade", value: "firmware-upgrade" },
    { label: "THs", value: "ths"},
    { label: "Rolling Firmware Upgrade", value: "rolling-firmware-upgrade" },
    { label: "QSE Query", value: "demandResponseQuery" },
    { label: "Power Curtail", value: "powercurtail" },
    { label: "Pool Config", value: "fluxvisionconfig"},
    { label: "Restart", value: "restart"},
    { label: "Reset Curtail Schedules", value: "exitpcschedule"},
    { label: "Recover Boot Partition", value: "reimage"},
    { label: "Factory Reset", value: "factory-reset"},
    { label: "Power", value: "power"},
  ];

  function getLabelByValue(value) {
    const found = commandList.find((item) => item.value === value);
    return found ? found.label : null;
  }

  const columns = [
    {
      field: "jobid",
      headerName: "Job ID",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "command",
      headerName: "Command",
      flex: 1,
      minWidth: 200,
            renderCell: (params) => {
        return (
          <>
            {params.value ? getLabelByValue(params.value) : "N/A"}
          </>
        );
      },
    },
    {
      field: "response",
      headerName: "Response",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "user",
      headerName: "User",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        return (
          <>
            {params.value ? displayTS(params.value) : "N/A"}
          </>
        );
      },
    },
  ];

  const dataGridProps = {
    rows: tableData,
    columns,
    disableColumnMenu: true,
    hideFooterPagination: true,
    hideFooter: true,
    pagination: true,
    pageSize: tableData.length,
    disableColumnResize: true,
    checkboxSelection: false,
    disableSelectionOnClick: true,
    loading: isJobsDetailsLoading || isJobsDetailsFetching,
    paginationModel: { page: 0, pageSize: totalCount },
    onPaginationModelChange: (model) =>
      console.log("Page changed:", model),
    disableCellSelection: true,
    onRowsScrollEnd: handleScrollEnd,
    overlayNoRowsTemplate: "No data available",
    slots: {
      loadingOverlay: () => <StyledLinearProgress colors={colors} />,
      noRowsOverlay: CustomGridRowsOverlay,
    },
    getRowId: (row) => row.jobid,
    slotProps: {
      loadingOverlay: {
        variant: "linear-progress",
        noRowsVariant: "skeleton",
        sx: {
          backgroundColor: colors.background.paper, 
        },
      },
    },
    getRowHeight: () => "auto",
  };

  return (
    <PageWrapper> 
      <HeaderContainer />
      <Grid
        container
        className={"miner-page-table-container"}
        display={"flex"}
        flexDirection={"row"}
        spacing={4}
        sx={{ transition: "all 0.3s ease-in-out" }}
      >
        <Grid
          ref={tableContainerRef}
          item
          xs={12}
          sx={{
            transition: "width 0.3s ease-in-out", // Smooth transition for width
          }}
        >
          <Box
            className={"miner-page-table"}
            display={"flex"}
            flexDirection={"row"}
            backgroundColor={colors?.background?.paper}
            padding={"0.75rem 0 0.75rem 0"}
            borderRadius={"1rem"}
            sx={{
              transition: "width 0.3s ease-in-out", // Smooth transition for width
            }}
          >
            <Box
              className={"table-container"}
              display={"flex"}
              flexDirection={"column"}
              padding={"0 0.75rem 0 0.75rem"}
              height={"auto"}
              // overflow={"auto"}
              minWidth={"0"}
              gap={4}
              flex={3}
              flexShrink={1}
            >
              <Grid
                container
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"flex-start"}
              >
                <Grid item xs={2}>
                  <Stack
                    direction={"column"}
                    spacing={3}
                    display={"flex"}
                    padding={
                      "0.5rem 0.75rem 0.5rem 0.75rem"
                    }
                    alignItems={"flex-start"}
                  >
                    <H8TitleMedium>
                      Chassis Serial Number
                    </H8TitleMedium>
                    <BodyMedium>{minerInfo && minerInfo.ChassisSerialNo ? minerInfo.ChassisSerialNo : ""}</BodyMedium>
                  </Stack>
                </Grid>
                <Grid item xs={2}>
                  <Stack
                    direction={"column"}
                    spacing={3}
                    display={"flex"}
                    padding={
                      "0.5rem 0.75rem 0.5rem 0.75rem"
                    }
                    alignItems={"flex-start"}
                  >
                    <H8TitleMedium>
                      Control Board Serial Number
                    </H8TitleMedium>
                    <BodyMedium>{trimmedSerialNo ? trimmedSerialNo : tableData && tableData[0].SerialNo ? tableData[0].SerialNo : ""}</BodyMedium>
                  </Stack>
                </Grid>
                <Grid item xs={2}>
                  <Stack
                    direction={"column"}
                    spacing={3}
                    display={"flex"}
                    padding={
                      "0.5rem 0.75rem 0.5rem 0.75rem"
                    }
                    alignItems={"flex-start"}
                  >
                    <H8TitleMedium>
                      IP Address
                    </H8TitleMedium>
                    <BodyMedium>{minerInfo && minerInfo.ip ?
                      <a
                        href={`https://${minerInfo.ip}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          cursor: "pointer",
                          textDecoration: "underline", // force underline
                          color: colors?.text?.primary,    
                          textDecorationColor: colors?.text?.primary, 
                        }}
                      >
                      {minerInfo.ip}
                      </a>
                    : ""}</BodyMedium>
                  </Stack>
                </Grid>
                <Grid item xs={2}>
                  <Stack
                    direction={"column"}
                    spacing={3}
                    display={"flex"}
                    padding={
                      "0.5rem 0.75rem 0.5rem 0.75rem"
                    }
                    alignItems={"flex-start"}
                  >
                    <H8TitleMedium>Model</H8TitleMedium>
                    <BodyMedium>{minerInfo && minerInfo.model ? minerInfo.model : ""}</BodyMedium>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
        <Grid
          ref={tableContainerRef}
          item
          xs={12}
          sx={{
            transition: "width 0.3s ease-in-out", // Smooth transition for width
          }}
        >
          <Box
            className={"miner-page-table"}
            display={"flex"}
            flexDirection={"row"}
            backgroundColor={colors?.background?.paper}
            padding={"0.75rem 0  0.75rem 0"}
            borderRadius={"1rem"}
            sx={{
              transition: "width 0.3s ease-in-out", // Smooth transition for width
            }}
          >
            <Box
              className={"table-container"}
              display={"flex"}
              flexDirection={"column"}
              padding={"0 0.75rem 0 0.75rem"}
              height={"auto"}
              minWidth={"0"}
              gap={4}
              flex={3}
              flexShrink={1}
            >
              <MinerJobDetailsPageHeaderCS
                searchValue={searchValue}
                onSearchChange={handleSearchChange}
                tableData={tableData}
              />
              <Box
                className={"Datagrid-container"}
                display={"flex"}
                flexShrink={1}
                flexGrow={1}
                height="calc(100vh - 25rem)"
              >
                <DataGrid
                  colors={colors}
                  config={dataGridProps}
                  height="calc(100vh - 25rem)"
                />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </PageWrapper>
  );
}

export default function MinerJobDetailsPageCS() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 0,
      },
      mutations: {
        retry: 0,
      },
    },
  });
  return (
    <QueryClientProvider client={queryClient}>
      <MinerJobDetailsPageContentCS />
    </QueryClientProvider>
  );
}
